import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  UsersIcon,
  WrenchIcon,
  BriefcaseIcon,
  ChartBarIcon,
  SettingsIcon,
  PinIcon,
  PinOffIcon,
} from '../icons.jsx';
import { useHasModuleAccess } from '../hooks/usePermission.js';

// Module list (top to bottom in sidebar). Admin is rendered separately below a divider.
const MODULES = [
  { code: 'workforce', label: 'Workforce', to: '/workforce', Icon: UsersIcon },
  { code: 'operations', label: 'Operations', to: '/operations', Icon: WrenchIcon },
  { code: 'client', label: 'Client', to: '/client', Icon: BriefcaseIcon },
  { code: 'analytics', label: 'Analytics', to: '/analytics', Icon: ChartBarIcon },
];

function NavItem({ to, label, Icon, expanded }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        [
          'group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
          expanded ? '' : 'justify-center',
          isActive
            ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-sm'
            : 'text-ink-600 hover:bg-sky-100',
        ].join(' ')
      }
      title={expanded ? undefined : label}
    >
      <Icon className="h-5 w-5 flex-shrink-0" />
      {expanded && <span className="truncate">{label}</span>}
    </NavLink>
  );
}

/**
 * Left sidebar navigation. Two modes:
 *   - pinned: sticky w-64 in flow, content reflows around it.
 *   - auto: w-16 spacer in flow + w-64 fixed overlay on hover (z-30, below header).
 *
 * Shows only modules the user has access to. Admin appears below a divider.
 */
export function ModuleNav({ pinned, onTogglePin }) {
  const hasAdmin = useHasModuleAccess('admin');
  const [hovered, setHovered] = useState(false);
  const expanded = pinned || hovered;

  const navContent = (
    <>
      <nav className="flex-1 space-y-1 overflow-y-auto p-3">
        {MODULES.map(({ code, label, to, Icon }) => (
          <ModuleGate key={code} moduleCode={code}>
            <NavItem to={to} label={label} Icon={Icon} expanded={expanded} />
          </ModuleGate>
        ))}

        {hasAdmin && (
          <>
            <div className="my-3 border-t border-sky-300" />
            <NavItem to="/admin" label="Admin" Icon={SettingsIcon} expanded={expanded} />
          </>
        )}
      </nav>

      <button
        type="button"
        onClick={onTogglePin}
        className="flex items-center justify-center gap-2 border-t border-sky-300 py-2.5 text-ink-500 transition-colors hover:bg-sky-100 hover:text-ink-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-400"
        aria-label={pinned ? 'Unpin sidebar' : 'Pin sidebar'}
        aria-pressed={pinned}
        title={pinned ? 'Unpin sidebar ([)' : 'Pin sidebar ([)'}
      >
        {pinned ? (
          <PinIcon className="h-5 w-5 text-blue-600" />
        ) : (
          <PinOffIcon className="h-5 w-5" />
        )}
        {expanded && (
          <span className="text-xs font-medium">{pinned ? 'Pinned' : 'Hover-only'}</span>
        )}
      </button>
    </>
  );

  if (pinned) {
    return (
      <aside className="sticky top-16 flex h-[calc(100vh-4rem)] w-64 flex-col border-r border-sky-300 bg-white">
        {navContent}
      </aside>
    );
  }

  // Auto mode: w-16 spacer keeps content offset, fixed overlay handles hover expansion.
  return (
    <>
      <div className="w-16 shrink-0" aria-hidden="true" />
      <aside
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setHovered(false);
        }}
        className={[
          'fixed left-0 top-16 z-30 flex h-[calc(100vh-4rem)] flex-col border-r border-sky-300 bg-white transition-[width] duration-150',
          hovered ? 'w-64 shadow-lg' : 'w-16',
        ].join(' ')}
      >
        {navContent}
      </aside>
    </>
  );
}

/**
 * Conditional wrapper — renders children only if user has access to the module.
 */
function ModuleGate({ moduleCode, children }) {
  const allowed = useHasModuleAccess(moduleCode);
  return allowed ? children : null;
}
